import { ComponentMultiStyleConfig } from '@chakra-ui/react';
import { StyleFunctionProps } from '@chakra-ui/theme-tools';

import { CardProps } from './Card';

import { FONT_SIZES, FONT_WEIGHTS } from 'theme/Typography';
import getElevation, { ElevationSizes } from 'theme/elevations';
import { getFocusedOutlineShadow } from 'theme/shadows';

const cardBorderRadius = '16px';
const cardPadding = '24px';

const disabledShadow = `0px 0px 0px 0.5px rgba(18, 21, 26, 0.16), 0px 1px 1px rgba(18, 21, 26, 0.04)`;

const CardTheme: ComponentMultiStyleConfig = {
  parts: ['card', 'cardContent', 'cardContentBody', 'cardContentHeading', 'cardFooter', 'cardHeader'],
  baseStyle: (props: CardProps & StyleFunctionProps) => ({
    card: {
      borderRadius: cardBorderRadius,
      boxShadow: getElevation(props, ElevationSizes.sm),
      minHeight: '150px',
      overflow: 'hidden',

      _light: {
        bg: 'silver.lightest',
        _disabled: {
          bg: 'silver.lighter',
          boxShadow: disabledShadow,
          cursor: 'not-allowed'
        }
      },
      _dark: {
        bg: 'black.lightest',
        _disabled: {
          bg: 'black.lightest',
          boxShadow: disabledShadow,
          cursor: 'not-allowed'
        }
      },
      ...(props.isClickable
        ? {
            cursor: 'pointer',
            _hover: {
              boxShadow: getElevation(props, ElevationSizes.lg)
            },
            _focus: {
              boxShadow: getFocusedOutlineShadow(props)
            },
            _focusVisible: {
              outline: 'none'
            }
          }
        : {})
    },
    cardContent: {
      minWidth: '250px',
      px: cardPadding,
      py: cardPadding,

      _light: {
        color: 'grey.base'
      },
      _dark: {
        color: 'silver.lightest'
      }
    },
    cardContentHeading: {
      fontSize: FONT_SIZES.lg,
      fontWeight: FONT_WEIGHTS.semibold,
      lineHeight: '25.5px',
      marginBottom: '8px',

      _light: {
        color: 'black.lighter',

        _disabled: {
          color: 'grey.light'
        }
      },
      _dark: {
        color: 'silver.lightest',

        _disabled: {
          color: 'grey.lightest'
        }
      }
    },
    cardContentBody: {
      fontSize: FONT_SIZES.md,
      lineHeight: '21px',

      _light: {
        color: 'grey.base',

        _disabled: {
          color: 'grey.light'
        }
      },
      _dark: {
        color: 'silver.lightest',

        _disabled: {
          color: 'grey.lightest'
        }
      }
    },
    cardFooter: {
      display: 'flex',
      justifyContent: 'flex-end',
      paddingBottom: cardPadding,
      px: cardPadding
    },
    cardHeader: {
      display: 'flex',
      paddingTop: cardPadding,
      px: cardPadding,

      _light: {
        color: 'black.lighter'
      },
      _dark: {
        color: 'silver.lightest'
      }
    }
  }),
  variants: {
    media: {
      cardHeader: {
        minHeight: '150px',
        px: 0,
        py: 0,
        '& img': {
          width: '100%'
        }
      }
    }
  }
};

export default CardTheme;
